/**
 * PublishWizardModal container (EXT-04 / Phase 6 / Plan 06-05, MOCK-ONLY).
 *
 * Owns the 3-step state machine + the mock publish progress timer + the mock
 * registry URL + the clipboard copy:
 *   step 0 — verify metadata (Mô tả + Phiên bản, version required to advance)
 *   step 1 — confirm package contents (checkbox required to advance)
 *   step 2 — mock progress 0→100% via setInterval → success + mock URL
 *
 * Pitfall 3 (timer leak): the progress interval is cleared in
 * componentWillUnmount AND on completion — covered by the unmount leak-guard
 * unit test (mirrors the smarthome-dashboard assertion).
 *
 * redux-throttle (300ms leading+trailing) coalesces rapid dispatches; we keep
 * the wizard progress in LOCAL state (not Redux) so the bar never gets
 * throttled, ticking at 300ms / +6% (~17 ticks ≈ 5.1s) per the firmware-wizard
 * precedent.
 */
import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';
import {compose} from 'redux';
import {injectIntl} from 'react-intl';

import {closePublishWizard} from '../reducers/modals';
import {showAlertWithTimeout} from '../reducers/alerts';

import {generateId, copyToClipboard} from '../lib/mstem-share-link';
import EXT_PUBLISH_PACKAGE from '../lib/mstem-mock/ext-publish-package';

import PublishWizardModalComponent from '../components/publish-wizard-modal/publish-wizard-modal.jsx';

// Tick at 300ms (above redux-throttle's 300ms window) / +6% per tick — the
// verified firmware-wizard cadence: ~17 ticks ≈ 5.1s, clamps to 100.
const TICK_INTERVAL_MS = 300;
const TICK_INCREMENT = 6;
const REGISTRY_URL_PREFIX = 'https://mstem.mobifone.vn/registry/';

const initialState = () => ({
    step: 0,
    description: '',
    version: '1.0.0',
    confirmed: false,
    progress: 0,
    isComplete: false,
    publishUrl: ''
});

class PublishWizardModal extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleDescChange',
            'handleVersionChange',
            'handleToggleConfirm',
            'handleNext',
            'handleCopyLink',
            'handleRequestClose',
            'tickProgress'
        ]);
        this._intervalId = null;
        this.state = initialState();
    }
    componentWillUnmount () {
        this.clearTimers();
    }
    clearTimers () {
        if (this._intervalId !== null) {
            clearInterval(this._intervalId);
            this._intervalId = null;
        }
    }
    startProgress () {
        this.clearTimers();
        this.setState({step: 2, progress: 0, isComplete: false});
        this._intervalId = setInterval(this.tickProgress, TICK_INTERVAL_MS);
    }
    tickProgress () {
        const next = Math.min(100, this.state.progress + TICK_INCREMENT);
        this.setState({progress: next});
        if (next >= 100) {
            this.clearTimers();
            this.setState({
                isComplete: true,
                publishUrl: REGISTRY_URL_PREFIX + generateId()
            });
        }
    }
    handleDescChange (value) {
        this.setState({description: value});
    }
    handleVersionChange (value) {
        this.setState({version: value});
    }
    handleToggleConfirm (checked) {
        // Called from the checkbox onChange (boolean) or directly in tests.
        this.setState({confirmed: typeof checked === 'boolean' ? checked : !this.state.confirmed});
    }
    handleNext () {
        if (this.state.step === 0) {
            if (this.state.version.trim().length > 0) {
                this.setState({step: 1});
            }
        } else if (this.state.step === 1) {
            if (this.state.confirmed) {
                this.startProgress();
            }
        }
    }
    handleCopyLink () {
        const url = this.state.publishUrl;
        copyToClipboard(url)
            .then(() => showAlertWithTimeout(this.props.dispatch, 'extPublishLinkCopied'))
            .catch(() => {}); // mock-only — copy failure is non-fatal
    }
    handleRequestClose () {
        this.clearTimers();
        this.setState(initialState());
        this.props.onClose();
    }
    render () {
        return (
            <PublishWizardModalComponent
                confirmed={this.state.confirmed}
                description={this.state.description}
                isComplete={this.state.isComplete}
                packageItems={EXT_PUBLISH_PACKAGE}
                progress={this.state.progress}
                publishUrl={this.state.publishUrl}
                step={this.state.step}
                version={this.state.version}
                onCopyLink={this.handleCopyLink}
                onDescChange={this.handleDescChange}
                onNext={this.handleNext}
                onRequestClose={this.handleRequestClose}
                onToggleConfirm={this.handleToggleConfirm}
                onVersionChange={this.handleVersionChange}
            />
        );
    }
}

PublishWizardModal.propTypes = {
    dispatch: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired
};

const mapStateToProps = () => ({});

const mapDispatchToProps = dispatch => ({
    dispatch,
    onClose: () => dispatch(closePublishWizard())
});

export default compose(
    injectIntl,
    connect(
        mapStateToProps,
        mapDispatchToProps
    )
)(PublishWizardModal);
